import type { LayerSpecification, LngLatLike, Map as MapBox, MapMouseEvent, MapOptions } from 'mapbox-gl';

import { LngLat, LngLatBounds, Marker } from 'mapbox-gl';

import { AllGeoJSON, bbox as turfBBox } from '@turf/turf';
import { Observable } from 'rxjs';
import { MapService } from '../map.service';

export type VisiblePoint = ReturnType<BaseLayer<unknown>['getCurrentVisiblePoints']>[number];

export const CLUSTER_DEFAULT_PARAMS = {
  cluster: true,
  clusterMaxZoom: 16,
  clusterMinZoom: 0,
  clusterRadius: 60,
  clusterMinPoints: 3,
};

interface InitOptions {
  cluster?: boolean;
}

interface ConstructorArgs<T> {
  extraProperties?: CreateFeatureCollectionArgs<Item<T>>['extraProperties'];
}
interface CreateFeatureCollectionArgs<T> {
  values: T[];
  extraProperties: (item: T) => Partial<T> | void;
}

type Properties = LayerSpecification & { source: string; type: LayerSpecification['type'] };

interface FeatureGeometry {
  latitude: number | null;
  longitude: number | null;
}

interface CustomFeatureProperties {
  uid: number;
  uid_parent: number;
  label?: string;
  icon?: number | string;
  geometry?: GeoJSON.Geometry;
  color?: string;
}

interface ClusterProperties {
  cluster?: true;
  cluster_id?: number;
  point_count?: number;
  point_count_abbreviated?: number;
}

type Item<T> = T & FeatureProperties & FeatureGeometry;
type FeatureProperties = CustomFeatureProperties & ClusterProperties;

/**
 * Allows to create and manage a layer in MapBox.
 */
class BaseLayer<T> {
  public mapService: Omit<MapService, 'map'> & { map: MapBox };

  public properties: Properties;
  public extraProperties: (item: T) => Partial<T> | void;
  public items: Observable<Item<T>[]>;
  public hided = true;
  public onSourceUpdate: () => void;

  public featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, FeatureProperties> = {
    type: 'FeatureCollection',
    features: [],
  };

  /**
   *
   * @param {Object} args -
   * @param {Object} args.items Writable interface for both updating and subscribing.
   * @param {Object} args.extraProperties Properties to add on feature collection if base properties is not enough.
   */
  constructor({ extraProperties }: ConstructorArgs<T>) {
    if (extraProperties) this.extraProperties = extraProperties;
  }

  /** BUILD */
  public init(options: InitOptions = { cluster: false }) {
    this.mapService.isLoaded(() => {
      this.createSource(options.cluster);
      this.create();

      this._createBaseEvents();

      this.items.subscribe(values => {
        if (values) {
          this.setFeatureCollection(values);

          if (!this.hided) this.updateSource();
        }
      });
    });
  }
  /**
   * Adds a Mapbox style layer to the map's style.
   */
  private create() {
    this.mapService.isLoaded(() => {
      if (this.mapService.map.getLayer(this.properties.id)) return;

      this.mapService.map.addLayer(this.properties);
    });
  }

  /**
   * Adds a source to the map's style.
   */
  private createSource(cluster?: boolean) {
    this.mapService.isLoaded(() => {
      const source = this.mapService.map.getSource(this.properties.source);
      if (source) return;

      this.mapService.map.addSource(
        this.properties.source,
        cluster
          ? {
              type: 'geojson',
              data: this.featureCollection,
              ...CLUSTER_DEFAULT_PARAMS,
            }
          : {
              type: 'geojson',
              data: this.featureCollection,
            }
      );
    });
  }

  /**
   * Sets the GeoJSON data and re-renders the map.
   */
  public updateSource(f: any = null) {
    const source = this.mapService.map.getSource(this.properties.source);
    if (source && source.type === 'geojson') source.setData(f ?? this.featureCollection);

    if (this.onSourceUpdate && !f) this.onSourceUpdate();
  }

  private checkCoordinatesValidity(longitude: number, latitude: number) {
    const isNotValid =
      longitude === undefined ||
      longitude === null ||
      latitude === undefined ||
      latitude === null ||
      (longitude === 0 && latitude === 0);

    return !isNotValid;
  }

  private _getFeatureByType(item: Item<T>): GeoJSON.Feature<GeoJSON.Geometry, FeatureProperties> {
    const { type } = this.properties;

    if (type === 'symbol' || type === 'circle') {
      return {
        id: item.uid,
        type: 'Feature',
        properties: {
          uid: item.uid,
          uid_parent: item.uid_parent,
          label: item.label || '',
          icon: item.icon || 0,
          ...this.extraProperties(item),
        },
        // TODO: create this directly into model data under key geometry
        geometry: {
          type: 'Point',
          coordinates: [item.longitude, item.latitude],
        },
      };
    }

    if (type === 'line' || type === 'fill') {
      return {
        id: item.uid,
        type: 'Feature',
        properties: {
          uid: item.uid,
          uid_parent: item.uid_parent,
          color: item.color,
        },
        geometry: item.geometry,
      };
    }

    throw new Error('Please give a layer type that is supported');
  }

  /**
   * Create a feature collection from raw data.
   * @param args -
   * @param args.values Raw data to convert.
   * @param args.extraProperties Properties to add on feature collection if base properties is not enough.
   */
  private async setFeatureCollection(items: Item<T>[]) {
    this.featureCollection = {
      type: 'FeatureCollection',
      features: [],
    };

    const { length } = items;

    for (let i = 0; i < length; i += 1) {
      const item = items[i];

      const isValid = this.checkCoordinatesValidity(item.longitude, item.latitude);

      if (isValid) this.featureCollection.features.push(this._getFeatureByType(item));
    }
  }

  /** ACTIONS */
  /**
   * Show the layer.
   */
  public show() {
    this.mapService.map.setLayoutProperty(this.properties.id, 'visibility', 'visible');
    this.hided = false;

    return this;
  }

  /**
   * Hide the layer.
   */
  public hide() {
    this.mapService?.map.setLayoutProperty(this.properties.id, 'visibility', 'none');
    this.hided = true;
    return this;
  }

  /**
   * Add a new Mapbox Marker to create a pulse highlight on item of layer.
   * @param {[number, number]} coordinates The coordinates where to put the highlight.
   * @param {number} size The size of the highlight.
   */
  public highlightItem(args: { coordinates: LngLatLike; size: number }) {
    this.mapService.deleteMarker('highlight');

    const node = document.createElement('div');
    const el = document.createElement('div');

    node.appendChild(el);
    el.style.outline = '2px solid #48C615';
    el.style.background = 'radial-gradient(circle, rgba(255,255,255,0) 0%, rgba(72,198,21,1) 100%)';
    el.style.width = `${args.size}px`;
    el.style.height = `${args.size}px`;
    el.style.borderRadius = '50%';
    el.classList.add('animate-ping');

    this.mapService.createMarker(
      'highlight',
      new Marker(node, { offset: [0, 0] }).setLngLat(args.coordinates || [0, 0])
    );

    return this;
  }

  private _getBoundsByType(type: LayerSpecification['type']) {
    if (type === 'symbol') {
      return this._getSymbolBounds();
    }

    if (type === 'line') {
      return turfBBox(this.featureCollection as AllGeoJSON);
    }

    throw new Error('Please give a supported layer type or implement this type');
  }

  private _getSymbolBounds() {
    const coordinates = this._extractValidCoordinates();

    if (coordinates.longitudes.length === 0 && coordinates.latitudes.length === 0) {
      return null;
    }

    return new LngLatBounds(
      new LngLat(Math.min(...coordinates.longitudes), Math.min(...coordinates.latitudes)),
      new LngLat(Math.max(...coordinates.longitudes), Math.max(...coordinates.latitudes))
    );
  }

  private _extractValidCoordinates() {
    const longitudes: number[] = [];
    const latitudes: number[] = [];

    for (const feature of this.featureCollection.features) {
      const { geometry } = feature;

      if (geometry.type === 'Point' && geometry.coordinates[0] && geometry.coordinates[1]) {
        const longitude = Number(geometry.coordinates[0]);
        const latitude = Number(geometry.coordinates[1]);

        if (longitude !== 0) longitudes.push(longitude);
        if (latitude !== 0) latitudes.push(latitude);
      }
    }

    return { longitudes, latitudes };
  }

  /**
   * Fit map to a bounding box of all existing coordinates items in layer.
   */
  public fit(options: MapOptions['fitBoundsOptions'] = {}) {
    const { features } = this.featureCollection;

    if (features?.length > 0) {
      const bounds = this._getBoundsByType(this.properties.type) as LngLatBounds;

      if (bounds) {
        this.mapService?.map.resize();
        this.mapService?.map.fitBounds(bounds, { padding: this.mapService.cameraPadding(20), ...options });
      }
    }

    return this;
  }

  /** UTILS */

  public getCurrentVisiblePoints() {
    const allFeatures = this.featureCollection.features;
    const visibleBounds = this.mapService.map.getBounds();

    return allFeatures.filter(f => {
      if (f.geometry.type === 'Point') {
        const [lng, lat] = f.geometry.coordinates;
        return visibleBounds.contains([lng, lat]);
      }
    });
  }

  /** EVENTS */
  /**
   * Trigger callback when a click event occurs on layer.
   * @param callback Operation to execute.
   */
  public onClick(callback: (event: MapMouseEvent) => void) {
    this.mapService?.map.on('click', this.properties.id, event => {
      callback(event);
    });
  }

  public onMouseEnter(callback: (event: MapMouseEvent) => void) {
    this.mapService.map.on('mouseenter', this.properties.id, event => {
      if (!this.hided) callback(event);
    });
  }

  public onMouseLeave(callback: (event: MapMouseEvent) => void) {
    this.mapService.map.on('mouseleave', this.properties.id, event => {
      if (!this.hided) callback(event);
    });
  }

  private _createBaseEvents() {
    this.mapService.map.on('mouseenter', this.properties.id, () => {
      const style = this.mapService.map.getCanvas().style;

      if (style) style.cursor = 'pointer';
    });

    // Change it back to a pointer when it leaves.
    this.mapService.map.on('mouseleave', this.properties.id, () => {
      const style = this.mapService.map.getCanvas().style;

      if (style) style.cursor = '';
    });
  }
}

export default BaseLayer;
